import _ from 'lodash';
import { DateTime } from 'luxon';

import { ValidationRule } from 'react-hook-form';



export const reUuid = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
export const rePhoneNumber = /^\+[0-9]{11,15}$/;
export const rePhoneNumberUsaDefault = /^(?:\+[0-9]{11,15}|[0-9]{10})$/;
export const reNonNegativeUsdAmount = /^\s*[0-9]{0,10}\.[0-9]{2}\s*$/;
export const reNonNegativeUsdAmountOrEmpty = /^(?:\s*[0-9]{0,10}\.[0-9]{2}\s*)$|^(?:\s*)$/;
export const reDecimalNumber = /^(?<left>[0-9]*)(?<right>\.[0-9]*)?$/;
export const reSsn = /^[0-9]{9}$/;
export const reZipCodeShort = /^[0-9]{5}$/;
export const reZipCodeLong = /^[0-9]{5}-[0-9]{4}$/;
export const reZipCodeShortOrLong = /^(?:[0-9]{5}|[0-9]{5}-[0-9]{4})$/;
// John Gruber's URL regular expression
// https://stackoverflow.com/questions/6927719/url-regex-does-not-work-in-javascript
export const reUrl = /^\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’]))$/;



const __messages = {
  phoneNumberFormat: "Please use the format \"+xxxxxxxxxxx\".",
  phoneNumberUsaDefaultFormat: "Please list 10 digits without symbols. (For international numbers, prefix with \"+\" followed by the country code.)",
  nonNegativeUsdAmountFormat: "Please use the format \"x.xx\". Example: \"20.00\".",
  zipCodeShortFormat: "Please enter exactly 5 digits.",
  zipCodeLongFormat: "Please use the format \"xxxxx-xxxx\".",
  zipCodeShortOrLongFormat: "Please enter exactly 5 digits or use the format \"xxxxx-xxxx\".",
  urlFormat: "Invalid URL format. Make sure it begins with either \"http://\" or \"https://\".",
  invalidNumber: "Invalid number.",
  wholeNumberRequired: "Must be a whole number.",
  required: "Required.",
  invalidDate: "Invalid date.",
  passwordsDoNotMatch: "Passwords don't match.",
  passwordLengthTooShort: "Must be at least 12 characters in length.",
  passwordLowercaseRequired: "Must have at least one lowercase letter.",
  passwordUppercaseRequired: "Must have at least one uppercase letter.",
  passwordNumeralRequired: "Must have at least one numeral.",
  passwordSymbolRequired: "Must have at least one symbol."
};



function __generateRules() {
  return {
    phoneNumber: {
      value: rePhoneNumber,
      message: __messages.phoneNumberFormat
    } as ValidationRule<RegExp>,

    phoneNumberUsaDefault: {
      value: rePhoneNumberUsaDefault,
      message: __messages.phoneNumberUsaDefaultFormat
    } as ValidationRule<RegExp>,

    nonNegativeUsdAmount: {
      value: reNonNegativeUsdAmount,
      message: __messages.nonNegativeUsdAmountFormat
    } as ValidationRule<RegExp>,

    nonNegativeUsdAmountOrEmpty: {
      value: reNonNegativeUsdAmountOrEmpty,
      message: __messages.nonNegativeUsdAmountFormat
    } as ValidationRule<RegExp>,

    zipCodeShort: {
      value: reZipCodeShort,
      message: __messages.zipCodeShortFormat
    } as ValidationRule<RegExp>,

    zipCodeLong: {
      value: reZipCodeLong,
      message: __messages.zipCodeLongFormat
    } as ValidationRule<RegExp>,

    zipCodeShortOrLong: {
      value: reZipCodeShortOrLong,
      message: __messages.zipCodeShortOrLongFormat
    } as ValidationRule<RegExp>,

    url: {
      value: reUrl,
      message: __messages.urlFormat
    } as ValidationRule<RegExp>,

    httpUrl: (value: string) => {
      if (!value) {
        return true;
      }

      if (!value.match(reUrl)) {
        return __messages.urlFormat;
      }

      if (!value.startsWith('http://') && !value.startsWith('https://')) {
        return __messages.urlFormat;
      }

      return true;
    },

    genIntegerRule: ({
      min, max
    }: {
      min?: number;
      max?: number;
    }) => {
      return (value: string) => {
        const iValue = +value;
        if (_.isNaN(iValue)) {
          return __messages.invalidNumber;
        } else if (!_.isInteger(iValue)) {
          return __messages.wholeNumberRequired;
        } else if (!_.isUndefined(min) && iValue < min) {
          return `Value cannot be less than ${min}.`;
        } else if (!_.isUndefined(max) && iValue > max) {
          return `Value cannot be greater than ${max}.`;
        } else {
          return true;
        }
      };
    },

    genNumberRule: ({
      min, max
    }: {
      min?: number;
      max?: number;
    }) => {
      return (value: string) => {
        const nValue = +value;
        if (_.isNaN(nValue)) {
          return __messages.invalidNumber;
        } else if (!_.isUndefined(min) && nValue < min) {
          return `Value cannot be less than ${min}.`;
        } else if (!_.isUndefined(max) && nValue > max) {
          return `Value cannot be greater than ${max}.`;
        } else {
          return true;
        }
      };
    },

    genDecimalNumberRule: ({
      min, max, maxNumLeftDigits, maxNumRightDigits
    }: {
      min?: number;
      max?: number;
      maxNumLeftDigits?: number;
      maxNumRightDigits?: number;
    }) => {
      return (value: string) => {
        const match = value.match(reDecimalNumber);
        if (!match) {
          return __messages.invalidNumber;
        }

        const leftDigits = match.groups?.left ?? "";
        if (leftDigits && !_.isUndefined(maxNumLeftDigits) && leftDigits.length > maxNumLeftDigits) {
          return `Cannot have more than ${maxNumLeftDigits} on the left side of the decimal.`;
        }

        const rightDigits = match.groups?.right ?? "";
        if (rightDigits && !_.isUndefined(maxNumRightDigits) && rightDigits.length > (maxNumRightDigits + 1)) {
          return `Cannot have more than ${maxNumRightDigits} on the right side of the decimal.`;
        }

        const nValue = +value;
        if (_.isNaN(nValue)) {
          return __messages.invalidNumber;
        } else if (!_.isUndefined(min) && nValue < min) {
          return `Value cannot be less than ${min}.`;
        } else if (!_.isUndefined(max) && nValue > max) {
          return `Value cannot be greater than ${max}.`;
        } else {
          return true;
        }
      };
    },

    genDateRule: ({
      min, max, isBirthDate, disallowPast, disallowFuture, format
    }: {
      min?: DateTime;
      max?: DateTime;
      isBirthDate?: boolean;
      disallowPast?: boolean;
      disallowFuture?: boolean;
      format?: string;
    }) => {
      return (value: Date | null) => {
        if (!value) {
          return true;
        }

        const dtNow = DateTime.now();
        const dtEarliestAllowedBirthDate = DateTime.fromISO('1850-01-01T00:00:00Z');
        const dtValue = DateTime.fromJSDate(value);

        if (disallowPast && dtValue < dtNow) {
          return "Cannot be in the past.";
        } else if ((isBirthDate || disallowFuture) && dtNow < dtValue) {
          return "Cannot be in the future.";
        } else if (isBirthDate && dtValue < dtEarliestAllowedBirthDate) {
          return "Date of birth is too early.";
        } else if (min && dtValue < min) {
          return `Cannot be earlier than ${__formatDate(min, format)}.`;
        } else if (max && dtValue > max) {
          return `Cannot be later than ${__formatDate(max, format)}`;
        }
      };
    },

    required: {
      value: true,
      message: __messages.required
    } as ValidationRule<boolean>,

    trimmedRequired: {
      validate: (value: string) => {
        const trValue = value.trim();
        return !trValue ?
          __messages.required :
          true;
      }
    },

    validDate: {
      validate: (value: Date | null) => {
        if (value && _.isNaN(value.getTime())) {
          return __messages.invalidDate;
        }
        return true;
      }
    }
  };
}



function __formatDate(dtValue: DateTime, format?: string) {
  return format ?
    dtValue.toFormat(format) :
    dtValue.toISODate();
}



export let rules = __generateRules();



/**
 * Must be called after `i18n` is initialized.
 */
export function init() {
  rules = __generateRules();
}



export function isValidPassword(password: string) {
  if (password.length < 12) {
    return __messages.passwordLengthTooShort;
  }

  if (!password.match(/[a-z]/)) {
    return __messages.passwordLowercaseRequired;
  }

  if (!password.match(/[A-Z]/)) {
    return __messages.passwordUppercaseRequired;
  }

  if (!password.match(/[0-9]/)) {
    return __messages.passwordNumeralRequired;
  }

  if (!password.match(/[!@#$%^&*()\-_+={}\[\]:;'"<>,.\\/?|`~]/)) {
    return __messages.passwordSymbolRequired;
  }

  return true;
}
